import React, { FunctionComponent, ReactNode } from 'react';
import classNames from 'classnames/bind';

import styles from './button.module.scss';
import { ThemeVariantType, ButtonSizeType } from '../../typings';
import { InternalLink } from '../internal-link/internal-link';
import Arrow from '../../assets/images/cta-arrow.inline.svg';
import AnchorLink from 'react-anchor-link-smooth-scroll';

type ButtonBorderTypes = 'rectangular' | 'rounded' | 'none_border' | 'bottom';
const cx = classNames.bind(styles);

export interface IButtonProps {
  theme?: ThemeVariantType;
  size: ButtonSizeType;
  border: ButtonBorderTypes;
  text?: string;
  boldText?: boolean;
  CTA?: boolean;
  children?: ReactNode;
  type?: 'button' | 'submit' | 'reset' | undefined;
  transofrmUppercase?: boolean;
  reversed?: boolean;
  internalLink: string | null;
  className?: string;
  scrollToSection?: string;
  externalLink?: string;
  downloadFile?: {
    url?: string;
    name?: string;
    description?: string;
  };
}

export const Button: FunctionComponent<IButtonProps> = props => {
  const {
    text,
    boldText,
    theme,
    size,
    border,
    CTA,
    children,
    type,
    transofrmUppercase,
    reversed,
    internalLink,
    className,
    scrollToSection,
    externalLink,
    downloadFile
  } = props;

  const classes = cx('button', border, size, {
    cta: CTA,
    boldText: boldText,
    transofrmUppercase: transofrmUppercase,
    reversed: reversed
  });

  const buttonContent = (
    <div className={styles.content}>
      {text || children}
      {CTA && (
        <div data-elem="arrowIcon" className={styles.arrowIcon}>
          <Arrow className={styles.icon} />
        </div>
      )}
    </div>
  );

  return (
    <div data-theme={theme && `theme-${theme}`}>
      {internalLink ? (
        <InternalLink
          url={
            scrollToSection
              ? `${internalLink}#${scrollToSection}`
              : internalLink
          }
        >
          <button className={`${classes} ${className}`}>{buttonContent}</button>
        </InternalLink>
      ) : downloadFile?.url ? (
        <a
          className={styles.externalLink}
          href={downloadFile.url}
          download={downloadFile.name}
          target="_blank"
          rel="noopener noreferrer"
        >
          <button
            className={`${classes} ${className} ${styles.downloadButton}`}
          >
            {buttonContent}
          </button>
        </a>
      ) : (
        <React.Fragment>
          {!internalLink && scrollToSection && scrollToSection?.length > 0 ? (
            <AnchorLink
              offset="100"
              className={styles.scrollToSection}
              href={`#${scrollToSection}`}
            >
              <button
                className={`${classes} ${className}`}
                type={type ? type : 'button'}
              >
                {buttonContent}
              </button>
            </AnchorLink>
          ) : externalLink ? (
            <a
              className={styles.externalLink}
              href={externalLink ? externalLink : undefined}
              target="_blank"
              rel="noopener noreferrer"
            >
              <button className={`${classes} ${className}`}>
                {buttonContent}
              </button>
            </a>
          ) : (
            <button className={`${classes} ${className}`}>
              {buttonContent}
            </button>
          )}
        </React.Fragment>
      )}
    </div>
  );
};
